/**
 * Audio Preloader Service
 * Handles preloading and caching of audio files for tests
 */

export interface AudioPreloadProgress {
  isLoading: boolean;
  loaded: number;
  total: number;
  currentFile?: string;
  error?: string;
}

export class AudioPreloaderService {
  private cache = new Map<string, string>(); // URL -> Blob URL
  private loadingPromises = new Map<string, Promise<void>>();
  
  /**
   * Preload all audio files with progress tracking
   */
  async preloadAllAudio(
    audioUrls: string[], 
    onProgress?: (progress: AudioPreloadProgress) => void
  ): Promise<void> {
    const uniqueUrls = Array.from(new Set(audioUrls.filter(Boolean)));
    
    if (uniqueUrls.length === 0) {
      onProgress?.({
        isLoading: false,
        loaded: 0,
        total: 0
      });
      return;
    }

    
    onProgress?.({
      isLoading: true,
      loaded: 0,
      total: uniqueUrls.length
    });

    let loaded = 0;
    const errors: string[] = [];

    // Load files sequentially to avoid overwhelming the server
    for (const url of uniqueUrls) {
      try {
        onProgress?.({
          isLoading: true,
          loaded,
          total: uniqueUrls.length,
          currentFile: this.getFileNameFromUrl(url)
        });

        await this.preloadSingleAudio(url);
        loaded++;
        
        
        onProgress?.({
          isLoading: true,
          loaded,
          total: uniqueUrls.length,
          currentFile: this.getFileNameFromUrl(url)
        });

      } catch (error) {
        errors.push(url);
        loaded++; // Still increment to continue progress
      }
    }

    const finalProgress: AudioPreloadProgress = {
      isLoading: false,
      loaded,
      total: uniqueUrls.length
    };

    if (errors.length > 0) {
      finalProgress.error = `Failed to load ${errors.length} audio file(s)`;
    } else {
    }

    onProgress?.(finalProgress);
  }

  /**
   * Preload a single audio file
   */
  private async preloadSingleAudio(url: string): Promise<void> {
    // Check if already cached
    if (this.cache.has(url)) {
      return;
    }

    // Check if already loading
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url)!;
    }

    const loadPromise = this.loadAudioFile(url);
    this.loadingPromises.set(url, loadPromise);

    try {
      await loadPromise;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  /**
   * Actually load and cache the audio file
   */
  private async loadAudioFile(url: string): Promise<void> {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'audio/*'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      
      // Verify it's actually audio
      if (!blob.type.startsWith('audio/')) {
      }

      const blobUrl = URL.createObjectURL(blob);
      this.cache.set(url, blobUrl);

    } catch (error) {
      // Store original URL as fallback
      this.cache.set(url, url);
      throw error;
    }
  }

  /**
   * Get cached audio URL (returns blob URL if cached, original URL if not)
   */
  getCachedAudioUrl(originalUrl: string): string {
    return this.cache.get(originalUrl) || originalUrl;
  }

  /**
   * Check if audio file is cached
   */
  isCached(url: string): boolean {
    return this.cache.has(url);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { cached: number; totalSize: string } {
    return {
      cached: this.cache.size,
      totalSize: `${this.cache.size} files` // Could calculate actual size if needed
    };
  }

  /**
   * Clear all cached audio files
   */
  clearCache(): void {
    
    // Clean up blob URLs to prevent memory leaks
    this.cache.forEach((blobUrl, originalUrl) => {
      if (blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    });
    
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Extract filename from URL for display purposes
   */
  private getFileNameFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'unknown';
      return filename;
    } catch {
      return url.substring(url.lastIndexOf('/') + 1) || 'unknown';
    }
  }
}

// Singleton instance
export const audioPreloader = new AudioPreloaderService();
